import { checkErrors, getDefaultOptions } from "./Category";

/**
 * An asynchronous task
 *
 * Used to track the execution status of an async task.
 */
export type Task = {
  readonly id?: number;
  parameters: {
    type: "prices/bulk_set" | "restictions/bulk_set";
  };
  readonly created_at?: string;
  readonly updated_at?: string;
  status: TaskStatus;
};

export enum TaskStatus {
  ERROR = "error",
  STARTED = "started",
  PENDING = "pending",
  FINISHED = "finished",
}

/**
 * Fetch a specific task from the API
 * @param taskId
 */
export const getTask = async (taskId: number): Promise<Task> => {
  const url = new URL(`/api/tasks/${taskId}/`, window.origin);

  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: "GET",
    })
  );

  checkErrors(response);

  return response.json();
};

export const watchTask = ({
  task,
  getTaskFn,
  onSuccess,
  onError,
  onFinish,
  delay = 5000,
  initialDelay = 2000,
  signal,
}: {
  task: Task | number;
  getTaskFn: (id: number) => Promise<Task>;
  onSuccess: (response: any) => void;
  onError?: (err: any) => void;
  onFinish?: () => void;
  delay?: number;
  initialDelay?: number;
  signal?: AbortSignal;
}): Promise<void> => {
  const id = typeof task === "number" ? task : (task.id as number);
  const firstDelay = initialDelay || delay;

  const poll = async (isFirst: boolean): Promise<void> => {
    if (signal?.aborted) return;

    try {
      const updatedTask = await getTaskFn(id);
      if (signal?.aborted) return;

      if (
        updatedTask.status === TaskStatus.PENDING ||
        updatedTask.status === TaskStatus.STARTED
      ) {
        const wait = isFirst ? firstDelay : delay;
        await new Promise((res) => setTimeout(res, wait));
        return poll(false); // continue polling
      }
      onSuccess(updatedTask);
    } catch (err) {
      if (!signal?.aborted && onError) {
        onError(err);
      }
    } finally {
      if (!signal?.aborted && onFinish) {
        onFinish();
      }
    }
  };

  return poll(true);
};
